import React from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import parse from "html-react-parser";
import { Button, ButtonToolbar, Modal, ModalProps } from "rsuite";

import "./ConfirmModal.less";

export const ConfirmationMode = {
    Default: "default",
    Destructive: "destructive",
} as const;

export type ConfirmationModeType =
    (typeof ConfirmationMode)[keyof typeof ConfirmationMode];

interface Props extends Omit<ModalProps, "onClose" | "enforceFocus"> {
    cancel?: string;
    confirm?: string;
    message: string;
    onCancel: () => void;
    onConfirm: () => void;
    mode?: ConfirmationModeType;
}

const ConfirmModal: React.FC<Props> = ({
    cancel,
    className,
    confirm,
    children,
    message,
    onCancel,
    onConfirm,
    mode = ConfirmationMode.Default,
    ...props
}: Props) => {
    const { t } = useTranslation();

    return (
        <Modal
            className={cn("confirm-modal", `confirm-modal--${mode}`, className)}
            enforceFocus
            onClose={onCancel}
            {...props}
        >
            <Modal.Header closeButton />

            <Modal.Body>
                <div className="confirm-modal__message">{parse(message)}</div>
                {children}
            </Modal.Body>

            <Modal.Footer>
                <ButtonToolbar className="confirm-modal__footer">
                    <Button appearance="primary" onClick={onConfirm}>
                        {confirm ?? t("confirmModal.confirm")}
                    </Button>

                    <Button appearance="ghost" onClick={onCancel}>
                        {cancel ?? t("confirmModal.cancel")}
                    </Button>
                </ButtonToolbar>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmModal;
